import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import * as maptilersdk from '@maptiler/sdk';
import maplibregl from 'maplibre-gl';

import { exaggeration, limit } from 'src/components/map/map';
import { activateAllActions } from 'src/utils/actions';
import { initBuffers } from 'src/utils/three/buffers';
import { initShaderProgram, setPositionAttribute, setColorAttribute } from 'src/utils/three/program';
import { arrayEquals, generateColors, getDistinctValues } from 'src/utils/arrayUtils';
import { SharedStateEvent, setSharedStateListener } from 'src/utils/shared-state';
import { SAFE_getCmap } from 'src/cmaps/cmaps';

export const TRACK_EXAGGERATION_MOD = 1.0
const zero_elev = 0


// TODO: Add to AwesomeDB and load from there
const sceneActions = {
    'Seal_Animation': {
        actions: {
            idleActionIdx: 9,
            glideActionIdx: 28,
            swimActionIdx: 15,
        },
    },
    'Seal._2': {
        actions: {
            idleActionIdx: 0,
            glideActionIdx: 0,
            swimActionIdx: 1,
        },
    },
};

const seal_state_rgb = {
    'Active Waking': [221, 241, 246],
    'Quiet Waking': [221, 241, 246],
    'HV Slow Wave Sleep': [131, 183, 200],
    'LV Slow Wave Sleep': [160, 247, 224],
    'Certain REM Sleep': [255, 240, 145],
    "Putative REM Sleep": [255, 240, 145]
}

export const seal_state_mapping = Object()
for (const property in seal_state_rgb) {
    seal_state_mapping[property] = seal_state_rgb[property].map(d => d / 255.0);
}

export function SAFE_get_seal_state_mapping(mapping, value, default_value = [1.0, 1.0, 1.0]) {
    if (Object.keys(mapping).includes(value)) {
        return mapping[value]
    }
    return default_value
}

function getModelTransform(track, idx) {
    let _idx = idx % limit
    var modelOrigin = [track[_idx].Long, track[_idx].Lat];
    // var modelAltitude = -track[_idx].Depth * 0.00001;
    // var modelAltitude = 2000;
    var modelAltitude = track[_idx].Depth * exaggeration * TRACK_EXAGGERATION_MOD;
    // var modelAltitude = 0;
    var modelRotate = [Math.PI / 2, 0, 0];

    var modelAsMercatorCoordinate = maptilersdk.MercatorCoordinate.fromLngLat(
        modelOrigin,
        modelAltitude
    );

    // transformation parameters to position, rotate and scale the 3D model onto the map
    var modelTransform = {
        translateX: modelAsMercatorCoordinate.x,
        translateY: modelAsMercatorCoordinate.y,
        translateZ: modelAsMercatorCoordinate.z,
        rotateX: modelRotate[0],
        rotateY: modelRotate[1],
        rotateZ: modelRotate[2],
        /* Since our 3D model is in real world meters, a scale transform needs to be
         * applied since the CustomLayerInterface expects units in MercatorCoordinates.
         */
        scale: 0.00001
        // scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits()
    };

    return {
        modelTransform,
        center: modelOrigin
    }
}


function getBuffers(track, gl, cmap, selectedVar, startTime = null, endTime = null) {
    const coords = track.map(d => {
        return {
            coord: maplibregl.MercatorCoordinate.fromLngLat({
                lng: d.Long,
                lat: d.Lat
            }),
            depth: d.Depth
        }
    }).map(d => {
        var modelAltitude = -d.depth * exaggeration * TRACK_EXAGGERATION_MOD;
        var modelAsMercatorCoordinate = maptilersdk.MercatorCoordinate.fromLngLat(
            [d.coord.x, d.coord.y],
            modelAltitude
        );
        return [
            d.coord.x, d.coord.y,
            modelAsMercatorCoordinate.z
        ]
    }).flat()
    let colors;
    if (startTime && endTime) {
        colors = track.map(d => {
            return [
                ...cmap[d[selectedVar]].map(i => i/255.0),
                d.R_Time > startTime && d.R_Time < endTime ? 1.0 : 0.0
            ]
        }).flat()
    }
    else {
        colors = track.map(d => {
            return [
                ...cmap[d[selectedVar]].map(i => i/255.0),
                1.0
            ]
        }).flat()
    }

    // create and initialize a WebGLBuffer to store vertex and color data
    const buffers = initBuffers(gl, coords, colors)
    return buffers
}



// configuration of the custom layer for a 3D model per the CustomLayerInterface
function customLayer(track, map, cmap, selectedVar) {

    const layer = {
        id: '3d-polyline',
        type: 'custom',
        renderingMode: '3d',
        map: map,
        track: track,
        cmap: cmap,
        selectedVar: selectedVar,
        onAdd(map, gl) {
            // create GLSL source for vertex shader
            const vertexSource = `#version 300 es
    
                uniform mat4 uProjectionMatrix;
                in vec3 aVertexPosition;
                in vec4 aVertexColor;
                out lowp vec4 vColor;

                void main() {
                    gl_Position = uProjectionMatrix * \
                        vec4(aVertexPosition, 1.0);
                    gl_PointSize = 10.0;
                    vColor = aVertexColor;
                }`;

            // create GLSL source for fragment shader
            const fragmentSource = `#version 300 es
                in lowp vec4 vColor;
                out lowp vec4 fragColor;

                void main(void) {
                    fragColor = vColor;
                }`;

            const shaderProgram = initShaderProgram(gl, vertexSource, fragmentSource)
            this.programInfo = {
                program: shaderProgram,
                attribLocations: {
                    vertexPosition: gl.getAttribLocation(shaderProgram, "aVertexPosition"),
                    vertexColor: gl.getAttribLocation(shaderProgram, "aVertexColor"),
                },
                uniformLocations: {
                    projectionMatrix: gl.getUniformLocation(
                        shaderProgram,
                        "uProjectionMatrix"
                    )
                },
            };

            // create and initialize a WebGLBuffer to store vertex and color data
            const safe_cmap = SAFE_getCmap(track, selectedVar)

            if (window.sharedState.window) {
                this.buffers = getBuffers(track, gl, safe_cmap, selectedVar, window.sharedState.window[0], window.sharedState.window[1])
            }
            else {
                this.buffers = getBuffers(track, gl, safe_cmap, selectedVar)
            }
            setSharedStateListener(SharedStateEvent.WINDOW_UPDATE, (state) => {
                if (state.window) {
                    this.buffers = getBuffers(track, gl, state.cmap, state.selectedVar, state.window[0], state.window[1])
                }
            })

            setSharedStateListener(SharedStateEvent.CMAP_UPDATE, (state) => {
                // this.buffers = getBuffers(track, gl, state.cmap, state.selectedVar, state.window[0], state.window[1])
                if (state.window) {
                    this.buffers = getBuffers(track, gl, state.cmap, state.selectedVar, state.window[0], state.window[1])
                }
                else {
                    this.buffers = getBuffers(track, gl, state.cmap, state.selectedVar)
                }
            })

            const canvas = document.getElementById('map');
            canvas.addEventListener('mousemove', (event) => {
                const x = event.clientX;
                const y = event.clientY;
                const canvasRect = map.getCanvas().getBoundingClientRect();
                const webglX = (x - canvasRect.left) * (map.getCanvas().width / canvasRect.width);
                const webglY = (canvasRect.bottom - y) * (map.getCanvas().height / canvasRect.height);

                const pixel = new Uint8Array(4);
                gl.readPixels(webglX, webglY, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
            });
            this.eventlistener = true
        },

        // method fired on each animation frame
        render(gl, matrix) {
            const modelOrigin = this.map.getCenter();  // just an example
            const modelElevation = 0;
            const modelOriginMercator = maplibregl.MercatorCoordinate.fromLngLat(modelOrigin, zero_elev);

            const terrainCenterElevation = this.map.transform.elevation;
            const deltaMetersZ = modelElevation - terrainCenterElevation;
            const mercatorPerMeter = modelOriginMercator.meterInMercatorCoordinateUnits();
            const deltaMercatorZ = deltaMetersZ * mercatorPerMeter;
            // console.log(deltaMercatorZ)

            var m = new THREE.Matrix4().fromArray(matrix);
            var l = new THREE.Matrix4()
                .makeTranslation(
                    0,
                    0,
                    deltaMercatorZ
                )
            gl.useProgram(this.programInfo.program);

            gl.uniformMatrix4fv(
                this.programInfo.uniformLocations.projectionMatrix,
                false,
                m.multiply(l).elements
            );
            setPositionAttribute(gl, this.buffers, this.programInfo);
            setColorAttribute(gl, this.buffers, this.programInfo);
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            gl.drawArrays(gl.POINT, 0, track.length);
        }
    };
    return layer
}

export default customLayer